import { AlertTriangle, ArrowLeft, Building2, CalendarClock, ExternalLink, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { openObjectRecord } from '@/lib/chatNavigation';
import {
  contractDaysLeft,
  contractOverdue,
  formatSedAmount,
  formatSedDate,
  formatSedDateTime,
  contractStatusView,
  sedDocumentTitle,
  type SedContract,
} from '@/lib/sed';
import { cn } from '@/lib/utils';
import { SedFilesBlock } from './SedFilesBlock';
import { SedRouteBlock } from './SedRouteBlock';
import { SedStatusChip } from './SedStatusChip';
import { useSedDocumentResources } from './useSedDocumentResources';

function Field({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={cn('min-w-0 rounded-xl border bg-background/60 px-2.5 py-2', wide && 'sm:col-span-2')}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 whitespace-pre-wrap break-words text-xs leading-5">{value || '—'}</div>
    </div>
  );
}

export function SedContractDetails({
  contract,
  onBack,
}: {
  contract: SedContract;
  onBack?: () => void;
}) {
  const resources = useSedDocumentResources(contract);
  const status = contractStatusView(contract);
  const overdue = contractOverdue(contract);
  const daysLeft = contractDaysLeft(contract);
  const period = [formatSedDate(contract.dateFrom), formatSedDate(contract.dateTo)]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-start gap-2 border-b bg-background/95 px-3 py-2.5 backdrop-blur">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Назад к списку"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Договор</span>
            <SedStatusChip code={status.code} label={status.label} fromRoute={status.fromRoute} />
            {overdue && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-700">
                <AlertTriangle className="h-3 w-3" /> просрочен
              </span>
            )}
          </div>
          <h2 className="mt-1 truncate text-base font-semibold leading-6">{sedDocumentTitle(contract)}</h2>
          <p className="truncate text-[11px] text-muted-foreground">
            {formatSedDate(contract.date) || 'Без даты'}
            {contract.createdAt ? ` · создан ${formatSedDateTime(contract.createdAt)}` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 rounded-xl text-xs"
          onClick={() => openObjectRecord('document', 'sed_contracts', contract.id)}
        >
          <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
          Открыть форму
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
        <section className="rounded-2xl border bg-card p-3">
          <div className="flex items-start gap-2">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Контрагент</div>
              <div className="break-words text-sm font-semibold">
                {contract.counterpartyName || 'Не указан'}
              </div>
            </div>
            {contract.amount && (
              <div className="shrink-0 text-right">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Сумма</div>
                <div className="inline-flex items-center gap-1 text-sm font-semibold tabular-nums text-primary">
                  <Wallet className="h-3.5 w-3.5" />
                  {formatSedAmount(contract.amount)}
                </div>
              </div>
            )}
          </div>
          {contract.subject && (
            <div className="mt-2 whitespace-pre-wrap break-words rounded-xl bg-muted/30 px-2.5 py-2 text-xs leading-5">
              {contract.subject}
            </div>
          )}
        </section>

        <section className="grid gap-2 sm:grid-cols-2">
          <div
            className={cn(
              'rounded-xl border px-2.5 py-2',
              overdue ? 'border-red-500/30 bg-red-500/10' : 'bg-background/60',
            )}
          >
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              <CalendarClock className="h-3 w-3" /> Срок действия
            </div>
            <div className={cn('mt-0.5 text-xs font-medium leading-5', overdue && 'text-red-700')}>
              {period || 'Не указан'}
            </div>
            {contract.dateTo && (
              <div className="text-[10px] text-muted-foreground">
                {overdue
                  ? 'срок истёк'
                  : daysLeft !== null && daysLeft >= 0
                    ? daysLeft === 0
                      ? 'истекает сегодня'
                      : `осталось ${daysLeft} дн.`
                    : ''}
              </div>
            )}
          </div>
          <Field label="Ответственный" value={contract.responsibleName} />
          <Field label="Регистрация" value={[contract.number ? `№ ${contract.number}` : '', formatSedDate(contract.date)].filter(Boolean).join(' · ')} />
          <Field label="Создал" value={contract.createdByName} />
          <Field label="Автопролонгация" value={contract.autoProlong ? 'Да' : 'Нет'} />
        </section>

        <SedRouteBlock
          details={resources.route}
          loading={resources.loadingRoute}
          error={resources.routeError}
          onOpenForm={() => openObjectRecord('document', 'sed_contracts', contract.id)}
        />

        <SedFilesBlock
          files={resources.files}
          loading={resources.loadingFiles}
          error={resources.filesError}
          downloadingId={resources.downloadingId}
          onDownload={(file) => void resources.download(file)}
        />

        <p className="px-1 text-[10px] leading-4 text-muted-foreground">
          Согласование, подписи и прикрепление файлов выполняются в форме договора — данные карточки обновятся после возврата.
        </p>
      </div>
    </div>
  );
}
